import { useMemo, useContext, useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  Grid,
  Typography,
  Alert,
  useTheme,
  Box,
  DialogActions,
  Stack,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
} from "@mui/material";

import { AppContext } from "../../contextProviders/AppContext";

import {
  getHours,
  isBefore,
  isValid,
  set,
  getMinutes,
  addHours,
  format,
  isSameDay,
} from "date-fns";
import {
  StaticDatePicker,
  StaticDateRangePicker,
} from "@mui/x-date-pickers-pro";
import { translation } from "../Dictionary";
import { Close } from "@mui/icons-material";
import { fi } from "date-fns/locale";
import TimePickerComponent from "./TimePickerComponent";

export default function DatePickerMobileDialog({
  getMinimumDate,
  openDateRangeMobile,
  returnTimeLabel,
  setOpenDateRangeMobile,
  startTimeLabel,
  handleChangeDate,
  deliveryBufferTime,
  layout,
  actionBar,
  startDateString,
  customDatePickerType,
  companyOpenHours,
  companyOpenHoursReturn,
  contentHeigth,
  dayStart,
  endDateString,
}) {
  const {
    language,
    selectedPicker,
    setSelectedPicker,
    selectedCompany,
    VUOKRAAPAKU_SPECIAL_COMPANY,
    selectedDateRange,
    selectedTimeFrame,
  } = useContext(AppContext);
  const theme = useTheme();

  function handleAdvance() {
    // handleSearch(selectedDateRange[0], selectedDateRange[1]);
    setOpenDateRangeMobile(false);
  }

  const deliveryBufferHoursLabel = useMemo(() => {
    if (selectedCompany?.deliveryBufferHours) {
      if (language === "fi") {
        return (
          <Typography variant="caption" align="center">
            Varaus voi alkaa aikaisintaan {selectedCompany?.deliveryBufferHours}{" "}
            tunnin päästä.
          </Typography>
        );
      }
      if (language === "se") {
        return (
          <Typography variant="caption" align="center">
            Du kan göra en reservation tidigast{" "}
            {selectedCompany?.deliveryBufferHours} timmar från nuvarande
            ögonblick.
          </Typography>
        );
      }
      if (language === "en") {
        return (
          <Typography variant="caption" align="center">
            You can make a reservation no earlier than{" "}
            {selectedCompany?.deliveryBufferHours} hours from the current
            moment.
          </Typography>
        );
      }
    }
    return null;
  }, [language, selectedCompany?.deliveryBufferHours]);

  return (
    <Dialog
      open={openDateRangeMobile}
      scroll="paper"
      PaperProps={{
        style: {
          borderRadius: "14px",
          margin: "10px",
        },
      }}
    >
      <DialogContent
        sx={{
          backgroundColor: theme.palette.primary.dialogBackground,
          padding: "10px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            position: "relative",
          }}
        >
          <Stack alignItems={"center"} flexDirection={"column"}>
            {deliveryBufferHoursLabel}
          </Stack>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              padding: "5px",
              flexDirection: "column",
            }}
          >
            <DatePicker
              customDatePickerType={customDatePickerType}
              getMinimumDate={getMinimumDate}
              handleChangeDate={handleChangeDate}
              deliveryBufferTime={deliveryBufferTime}
              layout={layout}
              actionBar={actionBar}
            />

            <TimePickers
              companyOpenHours={companyOpenHours}
              companyOpenHoursReturn={companyOpenHoursReturn}
              contentHeigth={contentHeigth}
              customDatePickerType={customDatePickerType}
              dayStart={dayStart}
              endDateString={endDateString}
              returnTimeLabel={returnTimeLabel}
              selectedDateRange={selectedDateRange}
              selectedPicker={selectedPicker}
              selectedTimeFrame={selectedTimeFrame}
              setSelectedPicker={setSelectedPicker}
              startDateString={startDateString}
              startTimeLabel={startTimeLabel}
            />
          </Box>

          <Grid container alignItems="center" direction="column">
            {VUOKRAAPAKU_SPECIAL_COMPANY && (
              <Grid item>
                <Alert
                  variant="filled"
                  //severity={startEndSameDay ? "info" : "success"}
                  style={{
                    transition: "350ms ease-in-out",
                  }}
                >
                  Voit halutessasi valita palautuksen seuraavalle päivälle
                </Alert>
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Stack
          direction={"row"}
          width={"100%"}
          justifyContent={"space-between"}
        >
          <Button color="primary" onClick={() => handleAdvance()}>
            {translation.get(language, "Cancel text")}
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => handleAdvance()}
            disabled={!isValid(selectedDateRange[1])}
            sx={{ borderBottomRightRadius: "10px" }}
          >
            OK
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

function StartTimePicker({ title }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const { selectedDateRange, setSelectedDateRange } = useContext(AppContext);

  const getFormattedTime = (date) => {
    if (isValid(date)) {
      return format(date, "HH:mm", {
        locale: fi,
      });
    }
    return "";
  };
  const formattedTime = getFormattedTime(selectedDateRange[0]);
  return (
    <>
      <Button
        variant="contained"
        sx={{
          borderRadius: "10px",
        }}
        fullWidth
        onClick={(e) => setAnchorEl(e.currentTarget)}
      >
        <Stack>
          <Typography>{title}</Typography>
          <Typography>{formattedTime}</Typography>
        </Stack>
      </Button>
      <Drawer
        id={"textIdPopper"}
        open={!!anchorEl}
        anchor="bottom"
        sx={{
          zIndex: 9000,
        }}
        fullWidth
      >
        <Box display={"flex"} width={"100%"} justifyContent={"flex-end"}>
          <IconButton onClick={() => setAnchorEl(null)}>
            <Close />
          </IconButton>
        </Box>
        <List>
          <ListItemButton
            onClick={() => {
              const newDate = handleChangeReservationDuration(
                8,
                selectedDateRange
              );
              setSelectedDateRange(newDate);
              setAnchorEl(null);
            }}
          >
            <ListItemText
              primary="8:00"
              primaryTypographyProps={{
                align: "center",
              }}
            />
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              const newDate = handleChangeReservationDuration(
                9,
                selectedDateRange
              );
              setSelectedDateRange(newDate);
              setAnchorEl(null);
            }}
          >
            <ListItemText
              primary="9:00"
              primaryTypographyProps={{
                align: "center",
              }}
            />
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              const newDate = handleChangeReservationDuration(
                13,
                selectedDateRange
              );
              setSelectedDateRange(newDate);
              setAnchorEl(null);
            }}
          >
            <ListItemText
              primary="13:00"
              primaryTypographyProps={{
                align: "center",
              }}
            />
          </ListItemButton>
          <ListItemButton
            onClick={() => {
              const newDate = handleChangeReservationDuration(
                14,
                selectedDateRange
              );
              setSelectedDateRange(newDate);
              setAnchorEl(null);
            }}
          >
            <ListItemText
              primary="14:00"
              primaryTypographyProps={{
                align: "center",
              }}
            />
          </ListItemButton>
        </List>
      </Drawer>
    </>
  );
}

const handleChangeReservationDuration = (startTime, selectedDateRange) => {
  const newStartTime = set(selectedDateRange[0], {
    hours: startTime,
    minutes: 0,
  });
  const newReturnTime = addHours(newStartTime, 5);

  return [newStartTime, newReturnTime];
};

function DatePicker({
  customDatePickerType,
  getMinimumDate,
  handleChangeDate,
  deliveryBufferTime,
  layout,
  actionBar,
}) {
  const {
    selectedCompany,
    selectedDateRange,
    setSelectedDateRange,
    isDisabled,
  } = useContext(AppContext);

  const datePickerState = customDatePickerType ? "range" : "single";

  switch (datePickerState) {
    case "range":
      return (
        <StaticDateRangePicker
          minDate={getMinimumDate()}
          displayStaticWrapperAs="desktop"
          calendars={1}
          disabled={!selectedCompany}
          open={true}
          disablePast
          onChange={(newValue) => setSelectedDateRange(newValue)}
          value={selectedDateRange}
          shouldDisableDate={(newValue, position) => {
            if (isDisabled(newValue)) {
              return true;
            }

            if (position === "end") {
              return (
                isSameDay(selectedDateRange[0], newValue) ||
                isDisabled(newValue)
              );
            }
          }}
          sx={{
            margin: 0,
            padding: 0,
            maxHeight: "320px",
          }}
          slots={{
            actionBar: actionBar,
            layout: layout,
          }}
        />
      );

    case "single":
      return (
        <StaticDatePicker
          minDate={getMinimumDate()}
          displayStaticWrapperAs="desktop"
          calendars={1}
          disabled={!selectedCompany}
          shouldDisableDate={isDisabled}
          open={true}
          disablePast
          onChange={(newValue) => handleChangeDate(newValue)}
          value={selectedDateRange}
          sx={{
            margin: 0,
            padding: 0,
            maxHeight: "320px",
          }}
          slots={{
            actionBar: actionBar,
            layout: layout,
          }}
        />
      );

    default:
      return null;
  }
}

function TimePickers({
  companyOpenHours,
  companyOpenHoursReturn,
  contentHeigth,
  customDatePickerType,
  dayStart,
  endDateString,
  returnTimeLabel,
  selectedDateRange,
  selectedPicker,
  selectedTimeFrame,
  setSelectedPicker,
  startDateString,
  startTimeLabel,
}) {
  const pickertype = customDatePickerType ? "range" : "single";

  switch (pickertype) {
    case "range":
      return (
        <Stack
          direction={"row"}
          sx={{
            width: "100%",
            justifyContent: "space-evenly",
            alignItems: "center",
          }}
          spacing={2}
        >
          <TimePickerComponent
            type={"startTime"}
            dateString={startDateString}
            contentHeigth={contentHeigth}
            selectedPicker={selectedPicker}
            companyOpenHours={companyOpenHours}
            companyOpenHoursReturn={companyOpenHoursReturn}
            setSelectedPicker={setSelectedPicker}
            title={startTimeLabel}
            selectedTimeFrame={selectedTimeFrame}
          />
          <TimePickerComponent
            contentHeigth={contentHeigth}
            selectedPicker={selectedPicker}
            setSelectedPicker={setSelectedPicker}
            companyOpenHours={companyOpenHours}
            companyOpenHoursReturn={companyOpenHoursReturn}
            selectedDateRange={selectedDateRange}
            type={"returnTime"}
            title={returnTimeLabel}
            dateString={endDateString}
            dayStart={dayStart}
            selectedTimeFrame={selectedTimeFrame}
          />
        </Stack>
      );

    case "single":
      return (
        <Stack width={"100%"} spacing={2} alignItems={"center"}>
          <StartTimePicker title={startTimeLabel} />

          <Stack direction={"row"} spacing={1}>
            <Typography>{returnTimeLabel}</Typography>
            <Typography>
              {isValid(selectedDateRange[1])
                ? format(selectedDateRange[1], "HH:mm", {
                    locale: fi,
                  })
                : ""}
            </Typography>
          </Stack>
        </Stack>
      );

    default:
      return null;
  }
}
